import React, { useId } from 'react';

interface RobotLogoProps {
  size?: number;
}

const RobotLogo: React.FC<RobotLogoProps> = ({ size = 36 }) => {
  const u = useId().replace(/:/g, '');

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'block', flexShrink: 0 }}
    >
      <defs>
        {/* 头部主体渐变 — 高级钛蓝金属 */}
        <linearGradient id={`head_${u}`} x1="20%" y1="0%" x2="80%" y2="100%">
          <stop offset="0%" stopColor="#A8D4FF" />
          <stop offset="20%" stopColor="#6BAAEE" />
          <stop offset="50%" stopColor="#4A8AD8" />
          <stop offset="80%" stopColor="#3670BA" />
          <stop offset="100%" stopColor="#2B5999" />
        </linearGradient>

        {/* 头部顶部高光 — 强烈金属反射 */}
        <linearGradient id={`headHL_${u}`} x1="30%" y1="0%" x2="70%" y2="100%">
          <stop offset="0%" stopColor="rgba(255,255,255,0.7)" />
          <stop offset="40%" stopColor="rgba(255,255,255,0.15)" />
          <stop offset="100%" stopColor="rgba(255,255,255,0)" />
        </linearGradient>

        {/* 眼睛发光 — 更亮更科技感 */}
        <radialGradient id={`eyeGlow_${u}`} cx="50%" cy="45%" r="50%">
          <stop offset="0%" stopColor="#FFFFFF" />
          <stop offset="30%" stopColor="#E0F3FF" />
          <stop offset="60%" stopColor="#90CAF9" />
          <stop offset="100%" stopColor="#42A5F5" />
        </radialGradient>

        {/* 眼睛外圈光晕 */}
        <radialGradient id={`eyeRing_${u}`} cx="50%" cy="50%" r="50%">
          <stop offset="60%" stopColor="rgba(100,181,246,0)" />
          <stop offset="85%" stopColor="rgba(100,181,246,0.3)" />
          <stop offset="100%" stopColor="rgba(100,181,246,0)" />
        </radialGradient>

        {/* 天线球渐变 — 暖橙更高级 */}
        <radialGradient id={`antBall_${u}`} cx="35%" cy="28%" r="65%">
          <stop offset="0%" stopColor="#FFAB91" />
          <stop offset="50%" stopColor="#FF7043" />
          <stop offset="100%" stopColor="#D84315" />
        </radialGradient>

        {/* 天线球光晕 */}
        <radialGradient id={`antGlow_${u}`} cx="50%" cy="50%" r="50%">
          <stop offset="50%" stopColor="rgba(255,112,67,0)" />
          <stop offset="100%" stopColor="rgba(255,112,67,0.25)" />
        </radialGradient>

        {/* 耳朵渐变 — 金属质感 */}
        <linearGradient id={`ear_${u}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#7BB8E0" />
          <stop offset="50%" stopColor="#4A8AD4" />
          <stop offset="100%" stopColor="#2E6AAF" />
        </linearGradient>

        {/* 嘴部网格渐变 */}
        <linearGradient id={`mouth_${u}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#345F8A" />
          <stop offset="100%" stopColor="#1E3D5C" />
        </linearGradient>

        {/* 底部阴影 */}
        <radialGradient id={`shadow_${u}`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgba(30,60,100,0.22)" />
          <stop offset="100%" stopColor="rgba(30,60,100,0)" />
        </radialGradient>

        {/* 整体外发光 */}
        <filter id={`glow_${u}`} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="1.5" result="blur" />
          <feComposite in="SourceGraphic" in2="blur" operator="over" />
        </filter>
      </defs>

      {/* === 底部投影 === */}
      <ellipse cx="32" cy="62" rx="18" ry="2.5" fill={`url(#shadow_${u})`} />

      {/* === 天线球外发光 === */}
      <circle cx="32" cy="4" r="6" fill={`url(#antGlow_${u})`} />

      {/* === 天线杆 === */}
      <rect x="30.5" y="4" width="3" height="10" rx="1.5" fill="#4A8AD4" />
      <rect x="31.2" y="4" width="1" height="10" rx="0.5" fill="rgba(255,255,255,0.35)" />

      {/* === 天线球 === */}
      <circle cx="32" cy="4" r="3.8" fill={`url(#antBall_${u})`} />
      <circle cx="30.5" cy="2.6" r="1.4" fill="rgba(255,255,255,0.6)" />
      <circle cx="31.2" cy="3.6" r="0.5" fill="rgba(255,255,255,0.3)" />

      {/* === 左耳 === */}
      <rect x="5.5" y="27" width="6" height="15" rx="3" fill={`url(#ear_${u})`} />
      <rect x="6.2" y="27.5" width="1.8" height="14" rx="0.9" fill="rgba(255,255,255,0.2)" />
      {/* 耳朵指示灯 */}
      <circle cx="8.5" cy="31" r="1.2" fill="#4FC3F7" opacity="0.7" />
      <circle cx="8.5" cy="31" r="0.5" fill="#fff" opacity="0.9" />

      {/* === 右耳 === */}
      <rect x="52.5" y="27" width="6" height="15" rx="3" fill={`url(#ear_${u})`} />
      <rect x="53.2" y="27.5" width="1.8" height="14" rx="0.9" fill="rgba(255,255,255,0.2)" />
      {/* 耳朵指示灯 */}
      <circle cx="55.5" cy="31" r="1.2" fill="#4FC3F7" opacity="0.7" />
      <circle cx="55.5" cy="31" r="0.5" fill="#fff" opacity="0.9" />

      {/* === 主头部 === */}
      <rect x="12" y="14" width="40" height="43" rx="11" fill={`url(#head_${u})`} />

      {/* 头部金属高光弧 */}
      <rect x="14" y="15" width="36" height="16" rx="9"
        fill={`url(#headHL_${u})`} />

      {/* 头部左边缘反射 */}
      <rect x="12" y="18" width="3" height="35" rx="1.5"
        fill="rgba(255,255,255,0.06)" />
      {/* 头部右边缘暗面 */}
      <rect x="49" y="18" width="3" height="35" rx="1.5"
        fill="rgba(0,0,0,0.04)" />

      {/* === 面板区域 — 内凹效果 === */}
      <rect x="17" y="24" width="30" height="19" rx="6.5"
        fill="rgba(0,0,0,0.1)" />
      <rect x="17.5" y="24.5" width="29" height="18" rx="6"
        fill="rgba(0,0,0,0.05)"
        stroke="rgba(255,255,255,0.06)" strokeWidth="0.5" />

      {/* === 左眼 === */}
      <circle cx="25" cy="33" r="7" fill={`url(#eyeRing_${u})`} />
      <ellipse cx="25" cy="33" rx="5.5" ry="5.8" fill="rgba(0,0,0,0.12)" />
      <ellipse cx="25" cy="32.6" rx="5" ry="5.3" fill={`url(#eyeGlow_${u})`} />
      <circle cx="25" cy="32.6" r="2.4" fill="#1565C0" />
      <circle cx="25" cy="32.6" r="1.1" fill="#0D47A1" />
      {/* 主反光 */}
      <circle cx="23.3" cy="30.8" r="1.2" fill="rgba(255,255,255,0.9)" />
      {/* 次反光 */}
      <circle cx="26.5" cy="34.2" r="0.5" fill="rgba(255,255,255,0.4)" />

      {/* === 右眼 === */}
      <circle cx="39" cy="33" r="7" fill={`url(#eyeRing_${u})`} />
      <ellipse cx="39" cy="33" rx="5.5" ry="5.8" fill="rgba(0,0,0,0.12)" />
      <ellipse cx="39" cy="32.6" rx="5" ry="5.3" fill={`url(#eyeGlow_${u})`} />
      <circle cx="39" cy="32.6" r="2.4" fill="#1565C0" />
      <circle cx="39" cy="32.6" r="1.1" fill="#0D47A1" />
      {/* 主反光 */}
      <circle cx="37.3" cy="30.8" r="1.2" fill="rgba(255,255,255,0.9)" />
      {/* 次反光 */}
      <circle cx="40.5" cy="34.2" r="0.5" fill="rgba(255,255,255,0.4)" />

      {/* === 嘴部 — 微笑弧线 === */}
      <rect x="22" y="45" width="20" height="8" rx="4" fill={`url(#mouth_${u})`} />
      <rect x="22.3" y="45.3" width="19.4" height="7.4" rx="3.7"
        stroke="rgba(255,255,255,0.1)" strokeWidth="0.4" fill="none" />
      {/* 栅格线 */}
      <line x1="23" y1="47.3" x2="41" y2="47.3" stroke="rgba(255,255,255,0.2)" strokeWidth="0.7" />
      <line x1="23" y1="49.3" x2="41" y2="49.3" stroke="rgba(255,255,255,0.2)" strokeWidth="0.7" />
      <line x1="23" y1="51.3" x2="41" y2="51.3" stroke="rgba(255,255,255,0.2)" strokeWidth="0.7" />
      <line x1="27" y1="45.5" x2="27" y2="52.5" stroke="rgba(255,255,255,0.1)" strokeWidth="0.5" />
      <line x1="32" y1="45.5" x2="32" y2="52.5" stroke="rgba(255,255,255,0.1)" strokeWidth="0.5" />
      <line x1="37" y1="45.5" x2="37" y2="52.5" stroke="rgba(255,255,255,0.1)" strokeWidth="0.5" />

      {/* === 额头装饰 — 六角螺栓 === */}
      <circle cx="19.5" cy="19.5" r="2" fill="rgba(255,255,255,0.15)" />
      <circle cx="19.5" cy="19.5" r="1.3" fill="rgba(255,255,255,0.25)" />
      <circle cx="19.5" cy="19.5" r="0.5" fill="rgba(255,255,255,0.45)" />
      <circle cx="44.5" cy="19.5" r="2" fill="rgba(255,255,255,0.15)" />
      <circle cx="44.5" cy="19.5" r="1.3" fill="rgba(255,255,255,0.25)" />
      <circle cx="44.5" cy="19.5" r="0.5" fill="rgba(255,255,255,0.45)" />

      {/* === 额头中心指示灯 === */}
      <circle cx="32" cy="19.5" r="2" fill="rgba(0,200,150,0.15)" />
      <circle cx="32" cy="19.5" r="1.2" fill="#26A69A" opacity="0.8" />
      <circle cx="31.5" cy="19" r="0.5" fill="rgba(255,255,255,0.7)" />

      {/* === 整体外框 — 精致细边 === */}
      <rect x="12" y="14" width="40" height="43" rx="11"
        stroke="rgba(255,255,255,0.15)" strokeWidth="0.6" fill="none" />
    </svg>
  );
};

export default RobotLogo;
